import React, { useEffect, useState } from "react";
import { 
  Typography, 
  CircularProgress, 
  Box, 
  Card, 
  CardContent, 
  CardMedia, 
  Grid, 
  List, 
  ListItem, 
  ListItemText, 
  Chip, 
  Divider,
  Container,
  Fade,
  Zoom,
  Avatar,
  Button
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getPlaneForUser } from "../../../Services/plansApi";
import { API_BASE_URL_images } from "../../../Services/apiConfig";
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { motion } from "framer-motion";

// Styled components for professional look
const ProfessionalCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: "24px",
  background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
  border: "1px solid rgba(0,0,0,0.05)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-16px) scale(1.03)",
    boxShadow: "0 25px 80px rgba(0,0,0,0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
  }
}));

const PriceBox = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  borderRadius: "20px",
  padding: "24px",
  color: "white",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
    animation: "shimmer 3s ease-in-out infinite",
  },
  "@keyframes shimmer": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" }
  }
}));

const FeatureItem = styled(ListItem)(({ theme }) => ({
  padding: "16px",
  margin: "8px 0",
  borderRadius: "16px",
  background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  position: "relative",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateX(-8px)",
    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    border: "2px solid rgba(102, 126, 234, 0.3)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "16px",
    padding: "2px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    maskComposite: "exclude",
    zIndex: -1
  }
}));

const PackagesPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPlaneForUser()
      .then((res) => {
        const palnes = res?.data?.data?.palnes || [];
        // احتفظ فقط بالخطط التي حالتها = 1
        const activePlans = palnes.filter((p) => Number(p.status) === 1);
        setPlans(activePlans);
      })
      .catch(() => {
        setPlans([]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <CircularProgress />;
  }

  if (plans.length === 0) {
    return <Typography>لا توجد باقات متوفرة حاليًا.</Typography>;
  }

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      py: 6
    }}>
      <Container maxWidth="xl">
        {/* Hero Section */}
        <Fade in={true} timeout={1000}>
          <Box sx={{ 
            textAlign: "center", 
            mb: 8,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "30px",
            p: 6,
            color: "white",
            position: "relative",
            overflow: "hidden"
          }}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  mx: "auto", 
                  mb: 3,
                  background: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)"
                }}
              >
                <WorkspacePremiumIcon sx={{ fontSize: 50, color: "white" }} />
              </Avatar>
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  textShadow: "0 4px 20px rgba(0,0,0,0.2)",
                  mb: 3
                }}
              >
                باقات مميزة لنجاح مطعمك
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  opacity: 0.9,
                  fontWeight: 400,
                  maxWidth: 800,
                  mx: "auto",
                  lineHeight: 1.6
                }}
              >
                اختر الباقة المناسبة لاحتياجاتك واستمتع بمميزات متقدمة لإدارة مطعمك بكفاءة عالية
              </Typography>
            </motion.div>
          </Box>
        </Fade>

        {/* Plans Grid */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={80} thickness={4} />
          </Box>
        ) : plans.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h5" color="text.secondary">
              لا توجد باقات متوفرة حاليًا
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {plans.map((plan, index) => (
              <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                <Zoom in={true} timeout={600 + index * 200}>
                  <ProfessionalCard>
                    {plan.image && (
                      <CardMedia
                        component="img"
                        height="240"
                        image={`${API_BASE_URL_images}/${plan.image}`}
                        alt={plan.plane_name}
                        sx={{ 
                          borderRadius: "24px 24px 0 0",
                          objectFit: "cover"
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                      <Box sx={{ textAlign: "center", mb: 4 }}>
                        <Typography
                          variant="h3"
                          gutterBottom
                          sx={{
                            fontWeight: "bold",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent"
                          }}
                        >
                          {plan.plane_name}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{ 
                            minHeight: "50px", 
                            lineHeight: 1.7,
                            fontSize: "1.1rem"
                          }}
                        >
                          {plan.desc}
                        </Typography>
                      </Box>

                      <PriceBox sx={{ mb: 4 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            mb: 1,
                            position: "relative",
                            zIndex: 1
                          }}
                        >
                          الشهري: {plan.monthly_price} جنيه
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            position: "relative",
                            zIndex: 1
                          }}
                        >
                          السنوي: {plan.yearly_price} جنيه
                        </Typography>
                        {plan.descount > 0 && (
                          <Chip
                            label={`وفر ${plan.descount}%`}
                            sx={{
                              mt: 2,
                              display: "block",
                              mx: "auto",
                              width: "fit-content",
                              fontWeight: "bold",
                              fontSize: "1rem",
                              backgroundColor: "rgba(255,255,255,0.2)",
                              color: "white"
                            }}
                          />
                        )}
                      </PriceBox>

                      {plan.features?.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: "bold",
                              color: "#1976d2",
                              mb: 3,
                              textAlign: "center"
                            }}
                          >
                            المميزات المتقدمة
                          </Typography>
                          <Divider sx={{ mb: 3, opacity: 0.3 }} />
                          <List sx={{ p: 0 }}>
                            {plan.features?.map((feature, featureIndex) => (
                               <motion.div
                                 key={feature.id}
                                 initial={{ opacity: 0, x: -20 }}
                                 animate={{ opacity: 1, x: 0 }}
                                 transition={{ delay: featureIndex * 0.1 }}
                               >
                                <FeatureItem>
                                  <Box sx={{ 
                                    display: "flex", 
                                    alignItems: "center", 
                                    width: "100%", 
                                    mb: 2
                                  }}>
                                    <Avatar sx={{
                                      width: 30,
                                      height: 30,
                                      background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
                                      mr: 2
                                    }}>
                                      <Typography sx={{ 
                                        color: "white", 
                                        fontSize: "14px", 
                                        fontWeight: "bold" 
                                      }}>
                                        ✓
                                      </Typography>
                                    </Avatar>
                                    <ListItemText
                                      primary={feature.name}
                                      sx={{
                                        "& .MuiListItemText-primary": {
                                          fontSize: "1.1rem",
                                          fontWeight: "600",
                                          color: "#333",
                                          lineHeight: 1.4
                                        }
                                      }}
                                    />
                                  </Box>
                                  
                                  <Box sx={{ 
                                    display: "flex", 
                                    justifyContent: "space-between", 
                                    width: "100%",
                                    gap: 2
                                  }}>
                                    <Chip
                                      label={`شهري: ${feature.pivot.monthly_cost} ج`}
                                      size="medium"
                                      sx={{
                                        background: "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)",
                                        color: "#2e7d32",
                                        fontWeight: "bold",
                                        fontSize: "0.9rem",
                                        flex: 1
                                      }}
                                    />
                                    <Chip
                                      label={`سنوي: ${feature.pivot.yearly_cost} ج`}
                                      size="medium"
                                      sx={{
                                        background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                                        color: "#1565c0",
                                        fontWeight: "bold",
                                        fontSize: "0.9rem",
                                        flex: 1
                                      }}
                                    />
                                  </Box>
                                </FeatureItem>
                              </motion.div>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: "20px",
                          py: 2,
                          fontSize: "1.2rem",
                          fontWeight: "bold",
                          textTransform: "none",
                          boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                          "&:hover": {
                            background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                            transform: "translateY(-3px)",
                            boxShadow: "0 15px 40px rgba(102, 126, 234, 0.5)"
                          }
                        }}
                      >
                        ترقية للباقة
                      </Button> */}
                    </CardContent>
                  </ProfessionalCard>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default PackagesPage;
